import { createContext, useContext, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const addItem = useCallback((product, size, color, qty = 1, extra = {}) => {
    setItems((prev) => {
      const key = `${product.id}-${size}-${color}-${extra.design_id || ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: extra.name || product.name,
          price: product.price,
          image: extra.image || product.image,
          size,
          color,
          color_key: extra.color_key || null,
          qty,
          design_id: extra.design_id || null,
        },
      ];
    });
    if (typeof window.fbq === "function") {
      window.fbq("track", "AddToCart", {
        content_ids: [product.id],
        content_name: extra.name || product.name,
        content_type: "product",
        value: Number((product.price * qty).toFixed(2)),
        currency: "USD",
      });
    }
    toast.success(`${extra.name || product.name} added to cart`);
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(0, i.qty + delta) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { total, count } = useMemo(() => {
    return items.reduce(
      (acc, i) => {
        acc.total += i.price * i.qty;
        acc.count += i.qty;
        return acc;
      },
      { total: 0, count: 0 }
    );
  }, [items]);

  const value = {
    items,
    open,
    setOpen,
    addItem,
    removeItem,
    updateQty,
    clear,
    total,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
